import { create } from 'zustand';

import { isTauri } from '@/lib/platform';

/**
 * Registers the offline service worker and reports when an update is ready.
 *
 * Production builds only, and never in the desktop app, which serves its
 * files from inside the binary and updates through its own updater
 * (`docs/DESKTOP.md`). In development a worker would serve yesterday's code
 * from its cache and make every edit look ignored.
 *
 * An update is never applied behind the pilot's back. A new worker installs,
 * then waits; `UpdateBanner` offers a reload, and only that reload swaps it
 * in — so a logbook half-edited in the grid is not reloaded out from under
 * its editor.
 */

interface UpdateState {
  waiting: ServiceWorker | null;
}

export const usePwaUpdate = create<UpdateState>(() => ({ waiting: null }));

let applying = false;

/** Swaps the waiting worker in and reloads once it has taken over. */
export function applyUpdate(): void {
  const { waiting } = usePwaUpdate.getState();
  if (waiting === null) {
    return;
  }
  applying = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

const UPDATE_CHECK_MS = 60 * 60 * 1000;

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || isTauri() || !('serviceWorker' in navigator)) {
    return;
  }
  const { serviceWorker } = navigator;

  // The first install also changes controller (the worker claims the page);
  // only a reload the pilot asked for should follow.
  serviceWorker.addEventListener('controllerchange', () => {
    if (applying) {
      applying = false;
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    void (async () => {
      const base = import.meta.env.BASE_URL;
      const registration = await serviceWorker.register(`${base}sw.js`, { scope: base });
      const offer = (worker: ServiceWorker | null) => {
        // No controller means this is the first install, not an update.
        if (worker !== null && serviceWorker.controller !== null) {
          usePwaUpdate.setState({ waiting: worker });
        }
      };
      offer(registration.waiting);
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        installing?.addEventListener('statechange', () => {
          if (installing.state === 'installed') {
            offer(installing);
          }
        });
      });
      // A pinned Home Screen app can stay open for days.
      setInterval(() => {
        void registration.update().catch(() => undefined);
      }, UPDATE_CHECK_MS);
    })().catch(() => {
      // No worker means no offline copy, not a broken app.
    });
  });
}
